import React, { useCallback } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { MDSButton, MDSButtonVariant } from "@/mds/components/buttons/button";
import { MDSButtonIcon, MDSButtonSlotName } from "@/mds/components/buttons/button";
import { MdsIconKind } from "@/design-system/components/icon";
import { SessionEventKind } from "../session.types";

interface MDSSessionFeedbackProps {
  /** The event ID to provide feedback for */
  eventId: string;
  /** The event kind to provide feedback for */
  eventKind: SessionEventKind;
  /** Handler function for feedback actions */
  onFeedback: (eventId: string, responseKind: "UPVOTE" | "DOWNVOTE") => void;
  /** Whether the feedback is selected */
  feedbackGiven?: "UPVOTE" | "DOWNVOTE" | null;
}

export const MDSSessionFeedback = observer<MDSSessionFeedbackProps>(function MDSSessionFeedback({
  eventId,
  onFeedback,
  feedbackGiven = null,
}) {
  const handleUpvoteClick = useCallback(() => {
    onFeedback(eventId, "UPVOTE");
  }, [eventId, onFeedback]);

  const handleDownvoteClick = useCallback(() => {
    onFeedback(eventId, "DOWNVOTE");
  }, [eventId, onFeedback]);

  return (
    <FeedbackContainer>
      <MDSButton
        variant={MDSButtonVariant.Transparent}
        onPress={handleUpvoteClick}
        aria-label="Mark response as good"
      >
        <MDSButtonIcon
          slot={MDSButtonSlotName.LeftIcon}
          kind={feedbackGiven === "UPVOTE" ? MdsIconKind.UpvoteSolid : MdsIconKind.Upvote}
        />
      </MDSButton>
      <MDSButton
        variant={MDSButtonVariant.Transparent}
        onPress={handleDownvoteClick}
        aria-label="Mark response as bad"
      >
        <MDSButtonIcon
          slot={MDSButtonSlotName.LeftIcon}
          kind={feedbackGiven === "DOWNVOTE" ? MdsIconKind.DownvoteSolid : MdsIconKind.Downvote}
        />
      </MDSButton>
    </FeedbackContainer>
  );
});

const FeedbackContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;
